import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  setSigninState,
  setUserData,
  setSalesforceToken,
  reset,
  setNavigationViews,
  setToolboxToken,
  setUserManagerData,
  setAuthz,
  setAuthProvider,
} from '../support/slices/userSlice';
import { LoginState } from '../types/Session';
import { CacheKey } from '../types/cache-keys';
import { fetchItems } from '../support/queries';
import { useCreateUserMutation, useFetchUser } from './user-hooks';
import { RootState, useAppDispatch } from '../support/store';
import { useNavigate } from 'react-router-dom';
import { removeCookie, setCookie } from '../support/cookieHelper';
import {
  hideReloginModal,
  beginSessionRecovery,
  sessionRecoveryFailed,
} from '../support/slices/uiSlice';
import { resetReauthGuard, isExpiredJwt } from '../support/queriesHelpers';

const toBool = (v?: string) => (v ?? '').toLowerCase() === 'true';

// Global singleton for periodic validator to avoid multiple intervals (StrictMode, multi mounts).
let VALIDATE_INTERVAL_STARTED = false;
let VALIDATE_INTERVAL_ID: ReturnType<typeof setInterval> | null = null;

// useAuthenticateUser is mounted in ~15 components, so every copy runs the recovery effect on the
// same 'attempting' state. This module-level latch ensures only the first instance triggers the
// auto re-auth redirect; it resets for free on the full-page reload the redirect causes.
let AUTO_REAUTH_TRIGGERED = false;

// OIDC storage keys
const PKCE_VERIFIER_KEY = 'duo_pkce_verifier';
const OIDC_STATE_KEY = 'duo_oidc_state';

// Salesforce OAuth state key
const SALESFORCE_OAUTH_STATE_KEY = 'salesforce_oauth_state';

// OAuth intent keys
const AUTH_FLOW_INTENT_KEY = 'auth_flow_intent';
const AUTH_FLOW_LOGIN_SF = 'login_salesforce';
const AUTH_FLOW_LINK_SF = 'link_salesforce';
const AUTH_FLOW_LOGIN_CISCO = 'login_cisco_sso';

// Auth in-flight marker
const AUTH_STARTED_AT_KEY = 'auth_started_at';
const AUTH_MAX_STALE_MS = 3 * 60 * 1000; // 3 minutes

// Auto re-auth loop-breaker. When a session expires we re-trigger the provider's OAuth authorize
// redirect (see the recovery effect) rather than a silent /session/validate — only the redirect can
// mint a fresh Salesforce access token. We stamp this key in localStorage (so it survives the
// full-page redirect the old per-instance ref could not) right before redirecting. If we land back
// in recovery within this window, another redirect would only bounce us again, so we escalate to the
// blocking manual overlay instead. The window must comfortably exceed one OAuth round-trip; a stamp
// older than this is treated as a genuine new expiry and auto-redirects again.
const AUTO_REAUTH_AT_KEY = 'auto_reauth_at';
const AUTO_REAUTH_MIN_INTERVAL_MS = 45 * 1000;

// Added: grace windows to avoid deployed race conditions
const AUTH_BOOTSTRAP_GRACE_MS = 15 * 1000; // skip restore/reset while auth is actively settling
const BROKEN_LOGGED_IN_GRACE_MS = 5 * 1000; // allow redux/store hydration to catch up
const OAUTH_EXCHANGE_TIMEOUT_MS = 20 * 1000; // abort a hung code->token exchange so login can't spin forever

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomString = (length = 96) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (v) => charset[v % charset.length]).join('');
};

const sha256 = async (value: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  return crypto.subtle.digest('SHA-256', encoder.encode(value));
};

const persistToolboxToken = (token: string) => {
  if (!token) return;

  setCookie('tb_jwt', token);
  setCookie('toolbox_jwt', token);
};

const generatePkcePair = async () => {
  const verifier = generateRandomString(96);
  const digest = await sha256(verifier);
  const challenge = base64UrlEncode(digest);
  return { verifier, challenge };
};

const setAuthInFlightMarker = () => {
  localStorage.setItem(AUTH_STARTED_AT_KEY, String(Date.now()));
};

const clearAuthInFlightMarker = () => {
  localStorage.removeItem(AUTH_STARTED_AT_KEY);
};

const getAuthInFlightAge = () => {
  const raw = localStorage.getItem(AUTH_STARTED_AT_KEY);
  if (!raw) return null;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return null;
  return Date.now() - ts;
};

export const useAuthenticateUser = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const signinState = useSelector((state: RootState) => state.user.signinState);
  const authProvider = useSelector((state: RootState) => state.user.authProvider);
  const { name, email, sfId } = useSelector((state: RootState) => state.user.userData);
  const salesforceToken = useSelector((state: RootState) => state.user.sfToken);
  const toolboxToken = useSelector((state: RootState) => state.user.toolboxToken);
  const navigationViews = useSelector((state: RootState) => state.user.navigationViews);
  const authz = useSelector((state: RootState) => state.user.authz);
  const sessionRecovery = useSelector((state: RootState) => state.ui.sessionRecovery);

  const {
    REACT_APP_SALESFORCE_CLIENT_ID,
    REACT_APP_SALESFORCE_REDIRECT_URI,
    REACT_APP_SALESFORCE_AUTH_URL,
    REACT_APP_API,
    REACT_APP_MOCK_SF_API,
    REACT_APP_LOCALHOST_USER,
    REACT_APP_DUO_OIDC_DISCOVERY_URL,
    REACT_APP_DUO_OIDC_CLIENT_ID,
    REACT_APP_DUO_OIDC_SCOPES,
    REACT_APP_USE_LOCAL_AUTH_API,
    REACT_APP_LOCAL_AUTH_API_BASE,
  } = process.env;

  const MOCK_API = toBool(REACT_APP_MOCK_SF_API);

  const isLocalHost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const useLocalAuthApi = toBool(REACT_APP_USE_LOCAL_AUTH_API);

  const AUTH_API_BASE = useLocalAuthApi
    ? REACT_APP_LOCAL_AUTH_API_BASE || 'http://127.0.0.1:8000/api/auth'
    : `${REACT_APP_API}/auth`;

  const isOAuthCallbackPath =
    window.location.pathname === '/oauth/_callback' ||
    window.location.pathname === '/ui/oauth/_callback' ||
    window.location.pathname === '/oidc/callback' ||
    window.location.pathname === '/oidc/callback/';

  const {
    data: user,
    isLoading: fetchingUser,
    isError: isUserFetchError,
    error: userFetchError,
  } = useFetchUser({ email, detailed: true }, { enabled: !!email });

  const { mutateAsync: createUser } = useCreateUserMutation({ username: name, email });

  const toolboxTokenRef = useRef<string | undefined | null>(toolboxToken);
  useEffect(() => {
    toolboxTokenRef.current = toolboxToken;
  }, [toolboxToken]);

  // Added: track when LoggedIn first occurs so we can allow a short hydration grace window
  const loggedInAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (signinState === LoginState.LoggedIn) {
      if (loggedInAtRef.current == null) {
        loggedInAtRef.current = Date.now();
      }
    } else {
      loggedInAtRef.current = null;
    }
  }, [signinState]);

  const clearStoredAuthArtifacts = useCallback(() => {
    removeCookie('sf_token');
    removeCookie('sf_email');
    removeCookie('tb_jwt');
    removeCookie('toolbox_jwt');
    removeCookie('tb_token');
    removeCookie('toolbox_token');
    removeCookie('toolboxToken');

    localStorage.removeItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(OIDC_STATE_KEY);
    localStorage.removeItem(SALESFORCE_OAUTH_STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OIDC_STATE_KEY);

    localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
    clearAuthInFlightMarker();

    // 'persist:user', not 'persist:root': the slices are persisted individually
    // (store.ts persists user, products, salesforcePermissions and moodle under their own
    // keys), so removing the root key cleared nothing and the toolbox JWT survived every
    // sign-out and session expiry in localStorage. store.ts's own 'persist:root' removal is
    // a one-time migration purge of a key this app no longer writes, and is left alone.
    localStorage.removeItem('persist:user');
  }, []);

  const clearTransientAuthArtifacts = useCallback(() => {
    localStorage.removeItem(PKCE_VERIFIER_KEY);
    localStorage.removeItem(OIDC_STATE_KEY);
    localStorage.removeItem(SALESFORCE_OAUTH_STATE_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OIDC_STATE_KEY);
    localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
    clearAuthInFlightMarker();
  }, []);

  const clearLocalSession = useCallback(() => {
    queryClient.removeQueries({ queryKey: [CacheKey.User] });
    clearStoredAuthArtifacts();
    dispatch(reset());
  }, [dispatch, queryClient, clearStoredAuthArtifacts]);

  const clearAuthState = useCallback(() => {
    clearLocalSession();
    dispatch(hideReloginModal());
  }, [clearLocalSession, dispatch]);

  const expireSession = useCallback(() => {
    // Non-destructive, matching refreshOn401 in queriesHelpers: do NOT reset the store. Keep
    // the session marked LoggedIn and the page mounted so recovery overlays wherever the user
    // is — including a deep URL they reopened into an already-expired session — and remember
    // that location so re-auth returns them to it instead of the app root. beginSessionRecovery
    // first attempts a silent restore; the blocking login overlay only appears if that fails.
    const here = window.location.pathname + window.location.search;
    if (here && here !== '/') {
      localStorage.setItem('postLoginPath', here);
    }
    queryClient.removeQueries({ queryKey: [CacheKey.User] });
    dispatch(beginSessionRecovery());
  }, [dispatch, queryClient]);

  const logout = useCallback(async () => {
    clearLocalSession();
    dispatch(hideReloginModal());
    navigate('/', { replace: true });
  }, [clearLocalSession, dispatch, navigate]);

  const failAuthentication = useCallback(() => {
    clearLocalSession();
    dispatch(setSigninState(LoginState.Error));
    dispatch(hideReloginModal());
    navigate('/', { replace: true });
  }, [clearLocalSession, dispatch, navigate]);

  const fullLogout = useCallback(() => {
    clearLocalSession();
    dispatch(hideReloginModal());
    navigate('/');
  }, [clearLocalSession, dispatch, navigate]);

  // Origin-based Salesforce callback URL. Both hosts (securitytoolbox.cisco.com and
  // securityhub.cisco.com) serve this same bundle but from different origins, and the SF connected
  // app registers a callback for each. A single build-time REACT_APP_SALESFORCE_REDIRECT_URI would
  // pin every login to one host, so a login started on the Hub would land back on the Toolbox
  // origin. Instead we keep the CONFIGURED callback path but swap in the current origin, mirroring
  // the Duo/OIDC path (see buildToolboxSessionFromCiscoSSO). The backend must exchange with this
  // same redirect_uri (OAuthCallbackView accepts a redirect_uri param, allowlisted) or SF 400s.
  const getSalesforceRedirectUri = useCallback((): string => {
    try {
      const configured = new URL(REACT_APP_SALESFORCE_REDIRECT_URI ?? '');
      return `${window.location.origin}${configured.pathname}`;
    } catch {
      return `${window.location.origin}/ui/oauth/_callback`;
    }
  }, [REACT_APP_SALESFORCE_REDIRECT_URI]);

  const getSalesforceAuthUrl = useCallback(() => {
    // Generate and persist a state value so the callback can validate it after Salesforce
    // redirects back.
    const state = generateRandomString(48);
    localStorage.setItem(SALESFORCE_OAUTH_STATE_KEY, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: REACT_APP_SALESFORCE_CLIENT_ID ?? '',
      // Origin-based so a login started on the Hub returns to the Hub (not the Toolbox origin).
      redirect_uri: getSalesforceRedirectUri(),
      scope: 'full',
      state,
    });
    return `${REACT_APP_SALESFORCE_AUTH_URL}?${params.toString()}`;
  }, [
    REACT_APP_SALESFORCE_CLIENT_ID,
    getSalesforceRedirectUri,
    REACT_APP_SALESFORCE_AUTH_URL,
  ]);

  const buildToolboxSessionFromSalesforce = useCallback(
    async (sfAccessToken: string, sfIdUrl?: string, sfInstanceUrl?: string) => {
      const tbResponse = await fetch(`${AUTH_API_BASE}/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',

          // Only for toolbox_auth token exchange.
          // Normal app APIs should use the Toolbox JWT returned below.
          Authorization: `Bearer ${sfAccessToken}`,
        },
        body: JSON.stringify({
          sf_token: sfAccessToken,
          id_url: sfIdUrl ?? '',
          instance_url: sfInstanceUrl ?? '',
        }),
      });

      if (!tbResponse.ok) throw new Error('Failed to get toolbox token');

      const tbData = await tbResponse.json();
      const receivedToolboxToken = tbData['access_token'];

      if (!receivedToolboxToken) throw new Error('No toolbox token received');

      dispatch(setToolboxToken(receivedToolboxToken));
      persistToolboxToken(receivedToolboxToken);

      const validateResp = await fetch(`${AUTH_API_BASE}/token/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${receivedToolboxToken}`,
        },
      });

      if (!validateResp.ok) throw new Error('Failed to validate toolbox token');

      const validated = await validateResp.json();

      const authzResp = await fetch(`${AUTH_API_BASE}/authz/context/`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${receivedToolboxToken}`,
        },
      });

      if (!authzResp.ok) throw new Error('Failed to load authz context');

      const authzData = await authzResp.json();
      dispatch(setAuthz(authzData));

      if (!validated.user || !validated.user.email) {
        throw new Error('No user info in validation');
      }

      const cleanEmail = validated.user.email.replace('.invalid', '');

      setCookie('sf_email', cleanEmail);

      const sfUserId =
        validated.user.user_id ||
        validated.user.sfId ||
        validated.user.sf_id ||
        validated.user.sub ||
        null;

      let sfUser: any[] = [];

      if (sfUserId) {
        try {
          sfUser = await fetchItems('sf/query', {
            q: `SELECT Id, Name, Manager.Id, Manager.Name, Manager.Email FROM User WHERE Id = '${sfUserId}'`,
          });
        } catch {
          sfUser = [];
        }
      }

      dispatch(
        setUserData({
          name: validated.user.display_name || validated.user.name || cleanEmail,
          email: cleanEmail,
          sfId: sfUserId,
        }),
      );

      dispatch(
        setUserManagerData({
          name: sfUser?.[0]?.Manager?.Name || null,
          email: sfUser?.[0]?.Manager?.Email?.replace('.invalid', '') || null,
        }),
      );

      if (validated.user.views) {
        dispatch(setNavigationViews(validated.user.views));
      }

      return {
        toolboxToken: receivedToolboxToken,
        validated,
        authz: authzData,
      };
    },
    [AUTH_API_BASE, dispatch],
  );

  const buildToolboxSessionFromCiscoSSO = useCallback(
    async (code: string, returnedState?: string) => {
      const codeVerifier =
        localStorage.getItem(PKCE_VERIFIER_KEY) || sessionStorage.getItem(PKCE_VERIFIER_KEY);
      const expectedState =
        localStorage.getItem(OIDC_STATE_KEY) || sessionStorage.getItem(OIDC_STATE_KEY);

      if (!codeVerifier) throw new Error('Missing PKCE verifier');
      if (!returnedState) throw new Error('Missing OIDC state from callback');
      if (!expectedState) throw new Error('Missing expected OIDC state from storage');
      if (returnedState !== expectedState) {
        throw new Error(`OIDC state mismatch: received=${returnedState} expected=${expectedState}`);
      }

      // The Cisco/Duo OIDC callback is the fixed SPA route on the SAME origin the flow was started
      // from. Always derive it from window.location.origin — never a build-time override — so a bundle
      // that baked the wrong REACT_APP_DUO_OIDC_REDIRECT_URI (e.g. the Salesforce /oauth/_callback path)
      // can't misroute the code, and the authorize/exchange redirect_uri stay byte-identical per env
      // (localhost:3001 / 127.0.0.1:3001 / dev.|uat.|securityhub.cisco.com — each registered in Duo).
      const redirectUri = `${window.location.origin}/oidc/callback/`;

      const requestBody = {
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
        state: returnedState,
      };

      const response = await fetch(`${AUTH_API_BASE}/cisco/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const rawText = await response.text();
      let data: any = {};

      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = { raw: rawText };
      }

      if (!response.ok) {
        throw new Error(data?.error || data?.detail || 'Failed to exchange Cisco SSO code');
      }

      const receivedToolboxToken =
        data?.access_token || data?.toolbox_token || data?.toolboxToken || data?.jwt;
      const ssoUser = data?.user;

      if (!receivedToolboxToken) throw new Error('No toolbox token received from Cisco SSO');

      localStorage.removeItem(PKCE_VERIFIER_KEY);
      localStorage.removeItem(OIDC_STATE_KEY);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);
      sessionStorage.removeItem(OIDC_STATE_KEY);
      localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
      clearAuthInFlightMarker();

      dispatch(setToolboxToken(receivedToolboxToken));
      persistToolboxToken(receivedToolboxToken);

      dispatch(setSalesforceToken(null));

      const cleanEmail = (ssoUser?.email || '').replace('.invalid', '');
      if (!cleanEmail) {
        throw new Error('No user email returned from Cisco SSO');
      }

      const authzResp = await fetch(`${AUTH_API_BASE}/authz/context/`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${receivedToolboxToken}`,
        },
      });

      if (!authzResp.ok) {
        throw new Error('Failed to load authz context');
      }

      const authzData = await authzResp.json();

      dispatch(setAuthz(authzData));

      dispatch(
        setUserData({
          name: ssoUser?.display_name || ssoUser?.name || cleanEmail,
          email: cleanEmail,
          sfId: null,
        }),
      );

      dispatch(
        setUserManagerData({
          name: null,
          email: null,
        }),
      );

      if (Array.isArray(ssoUser?.views)) {
        dispatch(setNavigationViews(ssoUser.views));
      }

      return {
        toolboxToken: receivedToolboxToken,
        authz: authzData,
        user: ssoUser,
      };
    },
    [AUTH_API_BASE, dispatch],
  );

  const login = useCallback(async () => {
    clearLocalSession();

    setAuthInFlightMarker();
    localStorage.setItem(AUTH_FLOW_INTENT_KEY, AUTH_FLOW_LOGIN_SF);

    dispatch(setAuthProvider('salesforce'));
    dispatch(setSigninState(LoginState.SigningIn));

    if (MOCK_API) {
      setCookie('sf_token', 'mock_token');
      dispatch(
        setUserData({
          name: 'Dev User',
          email: REACT_APP_LOCALHOST_USER ?? null,
          sfId: 'mock_sf_id',
        }),
      );
      dispatch(setSalesforceToken('mock_token'));
      dispatch(setToolboxToken('mock_tb_jwt'));
      dispatch(setAuthProvider('salesforce'));
      dispatch(setSigninState(LoginState.LoggedIn));
      clearAuthInFlightMarker();
      localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
      // No full-page reload on the mock path, so clear the 401 re-auth guard by hand
      // (the real redirect flows reload the app, which resets it for free).
      resetReauthGuard();
      navigate('/');
    } else {
      window.location.href = getSalesforceAuthUrl();
    }
  }, [clearLocalSession, dispatch, navigate, MOCK_API, REACT_APP_LOCALHOST_USER, getSalesforceAuthUrl]);

  const startSalesforceLinkLogin = useCallback(() => {
    setAuthInFlightMarker();
    localStorage.setItem(AUTH_FLOW_INTENT_KEY, AUTH_FLOW_LINK_SF);
    dispatch(setSigninState(LoginState.SigningIn));
    window.location.href = getSalesforceAuthUrl();
  }, [dispatch, getSalesforceAuthUrl]);

  const startCiscoSSOLogin = useCallback(async () => {
    clearLocalSession();

    setAuthInFlightMarker();
    localStorage.setItem(AUTH_FLOW_INTENT_KEY, AUTH_FLOW_LOGIN_CISCO);

    dispatch(setAuthProvider('cisco_sso'));
    dispatch(setSigninState(LoginState.SigningIn));

    const discoveryUrl = REACT_APP_DUO_OIDC_DISCOVERY_URL;
    const clientId = REACT_APP_DUO_OIDC_CLIENT_ID;
    // Same-origin, build-independent Cisco/Duo callback — see buildToolboxSessionFromCiscoSSO.
    const redirectUri = `${window.location.origin}/oidc/callback/`;
    const scopes = REACT_APP_DUO_OIDC_SCOPES ?? 'openid profile groups offline_access';

    if (!discoveryUrl || !clientId) {
      console.error('[Cisco SSO] Missing config', {
        discoveryUrl,
        clientIdExists: !!clientId,
        redirectUri,
      });
      clearAuthInFlightMarker();
      localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
      throw new Error('Missing Cisco SSO OIDC configuration');
    }

    const discoveryResp = await fetch(discoveryUrl);
    if (!discoveryResp.ok) {
      clearAuthInFlightMarker();
      localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
      throw new Error(`Failed to load OIDC discovery: ${discoveryResp.status}`);
    }

    const discovery = await discoveryResp.json();
    const authEndpoint = discovery?.authorization_endpoint;
    if (!authEndpoint) {
      clearAuthInFlightMarker();
      localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
      throw new Error('OIDC discovery missing authorization_endpoint');
    }

    const { verifier, challenge } = await generatePkcePair();
    const state = generateRandomString(48);

    localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    localStorage.setItem(OIDC_STATE_KEY, state);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(OIDC_STATE_KEY, state);

    const authUrl = new URL(authEndpoint);
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('scope', scopes);
    authUrl.searchParams.set('code_challenge', challenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('state', state);

    window.location.assign(authUrl.toString());
  }, [
    clearLocalSession,
    dispatch,
    REACT_APP_DUO_OIDC_DISCOVERY_URL,
    REACT_APP_DUO_OIDC_CLIENT_ID,
    REACT_APP_DUO_OIDC_SCOPES,
  ]);

  // Session-recovery state machine. When a 401 (or the periodic validator) puts recovery into
  // 'attempting', re-trigger the provider's full OAuth authorize redirect. This is the only path
  // that mints a fresh Salesforce access token — /session/validate merely revalidates the backend
  // cookie session and cannot fix the expired sf/* token that caused the 401, which is why the old
  // silent path looped. When the IdP session is still alive the redirect round-trips without any
  // user interaction; the overlay stays up throughout so nothing sensitive flashes, and
  // postLoginPath (captured by refreshOn401 / expireSession) returns the user to where they were.
  //
  // AUTO_REAUTH_TRIGGERED (module-level) keeps the ~15 mounted hook instances from each firing a
  // redirect. AUTO_REAUTH_AT_KEY (localStorage) survives the redirect's page reload: if we come
  // back and immediately re-enter recovery, another redirect would only bounce us again, so we
  // escalate to the blocking manual overlay instead.
  const recoveryAttemptRef = useRef(false);
  useEffect(() => {
    if (sessionRecovery !== 'attempting') {
      if (sessionRecovery === 'idle') {
        recoveryAttemptRef.current = false;
      }
      return;
    }
    if (recoveryAttemptRef.current) return;
    recoveryAttemptRef.current = true;

    // No real OAuth to run under the mock API; drop straight to the manual overlay.
    if (MOCK_API) {
      dispatch(sessionRecoveryFailed());
      return;
    }

    // Loop-breaker: we auto-redirected very recently yet are already back in recovery. Redirecting
    // again would loop; show the blocking manual overlay so the user can act (or truly re-log in).
    const lastAuto = Number(localStorage.getItem(AUTO_REAUTH_AT_KEY) || 0);
    if (lastAuto > 0 && Date.now() - lastAuto < AUTO_REAUTH_MIN_INTERVAL_MS) {
      localStorage.removeItem(AUTO_REAUTH_AT_KEY);
      dispatch(sessionRecoveryFailed());
      return;
    }

    if (AUTO_REAUTH_TRIGGERED) return;
    AUTO_REAUTH_TRIGGERED = true;

    // Stamp the attempt (survives the redirect reload) and re-trigger OAuth for the provider the
    // user signed in with. login() ends in a full-page Salesforce authorize redirect;
    // startCiscoSSOLogin() ends in a Cisco/Duo authorize redirect. Either tears the overlay down
    // by unloading the page. A Cisco user cannot recover via the Salesforce flow, so branch on it.
    localStorage.setItem(AUTO_REAUTH_AT_KEY, String(Date.now()));

    if (authProvider === 'cisco_sso') {
      void startCiscoSSOLogin().catch(() => {
        AUTO_REAUTH_TRIGGERED = false;
        localStorage.removeItem(AUTO_REAUTH_AT_KEY);
        dispatch(sessionRecoveryFailed());
      });
    } else {
      login();
    }
  }, [sessionRecovery, MOCK_API, authProvider, login, startCiscoSSOLogin, dispatch]);

  const handleOAuthCallback = useCallback(
    async (code: string) => {
      const authFlowIntent = localStorage.getItem(AUTH_FLOW_INTENT_KEY);
      const isLinkingSalesforce = authFlowIntent === AUTH_FLOW_LINK_SF;

      try {
        if (!isLinkingSalesforce) {
          clearLocalSession();
        }

        // Re-mark auth as in-flight after clearLocalSession() so downstream effects know
        // a fresh auth bootstrap is actively happening.
        setAuthInFlightMarker();

        dispatch(setAuthProvider('salesforce'));
        dispatch(setSigninState(LoginState.SigningIn));

        // Send the SAME origin-based redirect_uri used in the authorize request so the backend's
        // code->token exchange matches byte-for-byte (SF rejects a mismatch). Bundle is served from
        // this exact origin, so recomputing here yields the value authorize used.
        const redirectUri = getSalesforceRedirectUri();
        // Bound the exchange so a backend that never answers can't leave the callback route
        // spinning "Logging in..." forever. On timeout the fetch aborts and rejects into the catch
        // below, which fails the auth and returns the user to a usable login screen.
        const exchangeAbort = new AbortController();
        const exchangeTimeout = setTimeout(() => exchangeAbort.abort(), OAUTH_EXCHANGE_TIMEOUT_MS);
        let sfResponse: Response;
        try {
          sfResponse = await fetch(
            `${AUTH_API_BASE}/oauth/_callback?code=${encodeURIComponent(code)}&redirect_uri=${encodeURIComponent(redirectUri)}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: { Accept: 'application/json' },
              signal: exchangeAbort.signal,
            },
          );
        } finally {
          clearTimeout(exchangeTimeout);
        }

        if (!sfResponse.ok) throw new Error('OAuth callback failed');

        const sfData = await sfResponse.json();

        if (!sfData?.access_token) throw new Error('No Salesforce access token returned');

        setCookie('sf_token', sfData.access_token);
        dispatch(setSalesforceToken(sfData.access_token));

        await buildToolboxSessionFromSalesforce(
          sfData.access_token,
          sfData.id,
          sfData.instance_url,
        );

        dispatch(setSigninState(LoginState.LoggedIn));
        dispatch(hideReloginModal());
        localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
        clearAuthInFlightMarker();
      } catch (e) {
        console.error('Authentication callback failed:', e);
        localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
        clearAuthInFlightMarker();
        failAuthentication();
        throw e;
      }
    },
    [AUTH_API_BASE, dispatch, clearLocalSession, failAuthentication, buildToolboxSessionFromSalesforce, getSalesforceRedirectUri],
  );

  const handleCiscoSSOCallback = useCallback(
    async (code: string, returnedState?: string) => {
      try {
        // Re-mark auth as in-flight during callback exchange/bootstrap
        setAuthInFlightMarker();

        dispatch(setAuthProvider('cisco_sso'));
        dispatch(setSigninState(LoginState.SigningIn));
        dispatch(setSalesforceToken(null));

        await buildToolboxSessionFromCiscoSSO(code, returnedState);

        dispatch(setSigninState(LoginState.LoggedIn));
        dispatch(hideReloginModal());
        localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
        clearAuthInFlightMarker();

      } catch (e) {
        console.error('Cisco SSO callback failed:', e);
        localStorage.removeItem(AUTH_FLOW_INTENT_KEY);
        clearAuthInFlightMarker();
        failAuthentication();
        throw e;
      }
    },
    [dispatch, failAuthentication, buildToolboxSessionFromCiscoSSO],
  );

  useEffect(() => {
    if (MOCK_API) return;

    const path = window.location.pathname;
    const search = new URLSearchParams(window.location.search);

    const isSalesforceCallback =
      path === '/oauth/_callback' || path === '/ui/oauth/_callback';
    const isCiscoCallback =
      path === '/oidc/callback' || path === '/oidc/callback/';

    const hasCallbackCode = !!search.get('code');
    const hasCallbackState = !!search.get('state');

    const pkceVerifier =
      localStorage.getItem(PKCE_VERIFIER_KEY) || sessionStorage.getItem(PKCE_VERIFIER_KEY);
    const oidcState =
      localStorage.getItem(OIDC_STATE_KEY) || sessionStorage.getItem(OIDC_STATE_KEY);
    const salesforceOAuthState = localStorage.getItem(SALESFORCE_OAUTH_STATE_KEY);
    const authIntent = localStorage.getItem(AUTH_FLOW_INTENT_KEY);
    const authAge = getAuthInFlightAge();

    const isCiscoCallbackInFlight = isCiscoCallback && hasCallbackCode && hasCallbackState;
    const isCiscoFlowIntentActive = authIntent === AUTH_FLOW_LOGIN_CISCO;
    const isRecentAuthBootstrap = authAge != null && authAge < AUTH_BOOTSTRAP_GRACE_MS;

    if (isSalesforceCallback && !hasCallbackCode) {
      console.warn('[AUTH] Invalid Salesforce callback route without code. Resetting client auth state.');
      clearLocalSession();
      navigate('/', { replace: true });
      return;
    }

    if (isCiscoCallback && (!hasCallbackCode || !hasCallbackState)) {
      console.warn('[AUTH] Invalid Cisco callback route without code/state. Resetting client auth state.');
      clearLocalSession();
      navigate('/', { replace: true });
      return;
    }

    if (
      signinState === LoginState.SigningIn &&
      !isSalesforceCallback &&
      !isCiscoCallbackInFlight &&
      authAge != null &&
      authAge > AUTH_MAX_STALE_MS
    ) {
      console.warn('[AUTH] Stale SigningIn state detected. Clearing local auth session.');
      clearLocalSession();
      dispatch(hideReloginModal());
      navigate('/', { replace: true });
      return;
    }

    if (
      !isCiscoCallbackInFlight &&
      !isCiscoFlowIntentActive &&
      signinState !== LoginState.SigningIn &&
      !isRecentAuthBootstrap &&
      (pkceVerifier || oidcState || salesforceOAuthState || authIntent)
    ) {
      console.warn('[AUTH] Clearing stale transient auth artifacts.');
      clearTransientAuthArtifacts();
    }

    // Added: allow a short grace period after LoggedIn before treating partial state as broken.
    const loggedInAge =
      loggedInAtRef.current != null ? Date.now() - loggedInAtRef.current : null;

    const hasBrokenLoggedInState =
      signinState === LoginState.LoggedIn &&
      (!toolboxToken || !authz) &&
      !isRecentAuthBootstrap &&
      loggedInAge != null &&
      loggedInAge > BROKEN_LOGGED_IN_GRACE_MS;

    if (hasBrokenLoggedInState) {
      console.warn('[AUTH] LoggedIn state missing toolbox token or authz after grace window. Resetting.');
      clearLocalSession();
      navigate('/', { replace: true });
      return;
    }

    const brokenCiscoState =
      authProvider === 'cisco_sso' &&
      signinState !== LoginState.LoggedOut &&
      !toolboxToken &&
      !isCiscoCallbackInFlight &&
      !isCiscoFlowIntentActive &&
      !isRecentAuthBootstrap;

    const brokenSalesforceState =
      authProvider === 'salesforce' &&
      signinState !== LoginState.LoggedOut &&
      !salesforceToken &&
      !toolboxToken &&
      !isSalesforceCallback &&
      !isRecentAuthBootstrap;

    if (brokenCiscoState || brokenSalesforceState) {
      console.warn('[AUTH] Broken provider/token state detected. Resetting.');
      clearLocalSession();
      navigate('/', { replace: true });
    }
  }, [
    MOCK_API,
    signinState,
    authProvider,
    toolboxToken,
    salesforceToken,
    authz,
    clearLocalSession,
    clearTransientAuthArtifacts,
    dispatch,
    navigate,
  ]);

  // Silent restore on reload:
  // Salesforce only. Cisco SSO does not currently restore from a backend cookie session.
  useEffect(() => {
    if (MOCK_API) return;
    if (isOAuthCallbackPath) return;
    if (signinState === LoginState.SigningIn) return;

    const authFlowIntent = localStorage.getItem(AUTH_FLOW_INTENT_KEY);
    if (authFlowIntent === AUTH_FLOW_LOGIN_CISCO) return;

    const authAge = getAuthInFlightAge();
    const isRecentAuthBootstrap = authAge != null && authAge < AUTH_BOOTSTRAP_GRACE_MS;

    // Added: if a login/bootstrap just started or recently completed, do not let
    // session/validate race against the token/authz bootstrap flow.
    if (isRecentAuthBootstrap) {
      return;
    }

    const hasHydratedSession =
      !!email &&
      !!name &&
      !!toolboxToken &&
      !!authz &&
      (authProvider === 'cisco_sso' || (!!sfId && !!salesforceToken));

    if (hasHydratedSession) return;
    if (authProvider === 'cisco_sso') return;

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${AUTH_API_BASE}/session/validate/`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error('Session invalid');

        const data = await response.json();

        if (!data?.token || !data?.email || !data?.name || !data?.sfId) {
          throw new Error('Session validate missing required Salesforce session data');
        }

        if (cancelled) return;

        dispatch(setAuthProvider('salesforce'));
        dispatch(
          setUserData({
            name: data.name,
            email: data.email,
            sfId: data.sfId,
          }),
        );

        dispatch(setSalesforceToken(data.token));
        setCookie('sf_token', data.token);

        await buildToolboxSessionFromSalesforce(data.token, data.id_url);

        if (cancelled) return;

        dispatch(setSigninState(LoginState.LoggedIn));
        clearAuthInFlightMarker();
      } catch (e) {
        if (cancelled) return;

        // A silent RESTORE must never itself tear down the session. This path used to call
        // dispatch(reset()), which logged the user out shortly after signing in: the toolbox JWT is
        // deliberately NOT persisted (store.ts blacklists it for XSS safety), so on an early render
        // after login the in-memory session can briefly look un-hydrated, this best-effort cookie
        // revalidation runs, returns 401 (or a transient 429/5xx), and the reset wiped everything —
        // exactly the "logged out shortly after I log in" symptom.
        //
        // Genuine session expiry is already handled — non-destructively — by refreshOn401 /
        // expireSession when the next REAL authenticated request 401s (see expireSession, which
        // begins the recovery overlay + silent re-auth rather than resetting the store). So a failed
        // silent restore simply stops here: no reset, no logout, no store mutation of any kind.
        console.warn(
          '[AUTH] silent session/validate restore failed; leaving session intact (expiry is recovered on the next authenticated request).',
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    email,
    name,
    sfId,
    salesforceToken,
    toolboxToken,
    authz,
    signinState,
    authProvider,
    dispatch,
    AUTH_API_BASE,
    MOCK_API,
    isOAuthCallbackPath,
    buildToolboxSessionFromSalesforce,
    clearTransientAuthArtifacts,
  ]);

  useEffect(() => {
    if (fetchingUser || !email) return;

    if (authProvider === 'salesforce' && !salesforceToken) return;
    if (authProvider === 'cisco_sso' && !toolboxToken) return;

    // Only treat an Unauthorized user fetch as a genuine mid-session expiry once the toolbox JWT is
    // actually hydrated. On reload the JWT is not persisted (store.ts blacklists it) and is re-minted
    // by the silent restore; the user query can fire and 401 during that window (it falls back to the
    // Salesforce token, which the db/ backend rejects). Expiring here in that window bounces the user
    // into recovery/OAuth — the "reload logs me out immediately" symptom. With no toolbox token yet,
    // let the silent restore finish (or refreshOn401 recover post-bootstrap) instead of expiring.
    if (isUserFetchError && userFetchError === 'Unauthorized' && !!toolboxToken) {
      expireSession();
      return;
    }

    if (!user && [LoginState.SalesforceConnected, LoginState.LoggedIn].includes(signinState) && email) {
      createUser({ email, username: name });
    }

    if (user && [LoginState.SalesforceConnected, LoginState.LoggedIn].includes(signinState)) {
      dispatch(setSigninState(LoginState.LoggedIn));
      dispatch(setUserData({
        name: user.name,
        email: user.email,
        sfId,
        isManager: user.is_manager ?? false,
        id: user.id,
        manager: user.manager,
      }));
    }
  }, [
    user,
    fetchingUser,
    signinState,
    email,
    name,
    salesforceToken,
    toolboxToken,
    authProvider,
    isUserFetchError,
    userFetchError,
    createUser,
    expireSession,
    dispatch,
    sfId,
  ]);

  // Mirror the re-auth modal flag into a ref so validateSession can read it without
  // re-subscribing. Once expireSession has raised the modal there is nothing to gain from
  // re-validating (or re-expiring) every 60s against the same dead token — the modal is up
  // and blocking until the user re-authenticates.
  const reloginModalShown = useSelector((s: RootState) => s.ui.showReloginModal);
  const reloginModalShownRef = useRef(reloginModalShown);
  useEffect(() => {
    reloginModalShownRef.current = reloginModalShown;
  }, [reloginModalShown]);

  // Once recovery is underway ('attempting' or 'failed') there is nothing to gain from
  // re-validating (or re-expiring) against the same dead token: silent re-auth is running or the
  // login overlay is up and blocking until the user re-authenticates.
  const sessionRecoveryRef = useRef(sessionRecovery);
  useEffect(() => {
    sessionRecoveryRef.current = sessionRecovery;
  }, [sessionRecovery]);

  const lastValidateAtRef = useRef<number>(0);
  const validateSession = useCallback(async () => {
    if (MOCK_API) return;
    if (isOAuthCallbackPath) return;
    if (reloginModalShownRef.current) return;
    if (sessionRecoveryRef.current !== 'idle') return;

    const INTERVAL_MS = 60_000;
    const now = Date.now();
    if (now - lastValidateAtRef.current < INTERVAL_MS - 100) {
      return;
    }
    lastValidateAtRef.current = now;

    const tbJwt = toolboxTokenRef.current;
    if (!tbJwt) {
      expireSession();
      return;
    }

    try {
      const resp = await fetch(`${AUTH_API_BASE}/token/validate/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${tbJwt}`,
        },
      });

      if (!resp.ok) {
        expireSession();
      }
    } catch {
      expireSession();
    }
  }, [MOCK_API, AUTH_API_BASE, isOAuthCallbackPath, expireSession]);

  // Proactive expiry: the periodic validateSession only runs on a 60s cadence, so a toolbox JWT that
  // expires mid-session would keep the app in a "logged in but not usable" limbo (every db/ai/queue
  // request silently falls back to the Salesforce token and 403s) until the next tick. Detect the
  // spent JWT directly and start recovery immediately. expireSession is non-destructive and no-ops
  // while recovery is already under way.
  useEffect(() => {
    if (MOCK_API) return;
    if (isOAuthCallbackPath) return;
    if (sessionRecovery !== 'idle') return;
    if (reloginModalShownRef.current) return;

    if (signinState === LoginState.LoggedIn && !!toolboxToken && isExpiredJwt(toolboxToken)) {
      expireSession();
    }
  }, [MOCK_API, isOAuthCallbackPath, sessionRecovery, signinState, toolboxToken, expireSession]);

  useEffect(() => {
    if (MOCK_API) return;
    if (isOAuthCallbackPath) return;

    if (signinState === LoginState.LoggedIn && !!toolboxToken) {
      if (!VALIDATE_INTERVAL_STARTED) {
        const INTERVAL_MS = 60_000;

        VALIDATE_INTERVAL_STARTED = true;
        VALIDATE_INTERVAL_ID = setInterval(() => {
          validateSession();
        }, INTERVAL_MS);

        validateSession();
      }
    } else {
      if (VALIDATE_INTERVAL_STARTED && VALIDATE_INTERVAL_ID) {
        clearInterval(VALIDATE_INTERVAL_ID);
        VALIDATE_INTERVAL_ID = null;
        VALIDATE_INTERVAL_STARTED = false;
        lastValidateAtRef.current = 0;
      }
    }
  }, [MOCK_API, signinState, toolboxToken, isOAuthCallbackPath, validateSession]);

  return {
    email,
    name,
    sfId,
    authProvider,
    salesforceToken,
    toolboxToken,
    navigationViews,
    signinState,
    authz,

    login,
    logout,
    fullLogout,
    startSalesforceLinkLogin,

    handleOAuthCallback,
    startCiscoSSOLogin,
    handleCiscoSSOCallback,

    clearAuthState,
  };
};